import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  appearanceImageUrl,
  listAppearance,
  pickAppearance,
  type AppearanceSample,
  type DatasetSplit,
} from '../../api/appearanceReview';

const FIELDS: { key: string; label: string }[] = [
  { key: 'age', label: '年龄' },
  { key: 'gender_presentation', label: '性别呈现' },
  { key: 'height_cm', label: '身高 cm' },
  { key: 'weight_kg', label: '体重 kg' },
  { key: 'chest_cm', label: '胸围 cm' },
  { key: 'waist_cm', label: '腰围 cm' },
  { key: 'hip_cm', label: '臀围 cm' },
  { key: 'shoulder_cm', label: '肩宽 cm' },
  { key: 'foot_length_cm', label: '脚长 cm' },
  { key: 'body_shape', label: '身型' },
  { key: 'upper_to_lower', label: '上下身比例' },
  { key: 'skin_tone_depth', label: '肤色深浅' },
  { key: 'skin_undertone', label: '肤色底调' },
  { key: 'hair_color', label: '发色' },
  { key: 'hair_length', label: '发长' },
  { key: 'hair_texture', label: '发质' },
  { key: 'hair_volume', label: '发量' },
  { key: 'face_shape', label: '脸型' },
  { key: 'facial_feature_weight', label: '五官量感' },
];

const WORDS: Record<string, string> = {
  female: '女', male: '男',
  hourglass: '沙漏', rectangle: '矩形', triangle: '正三角', inverted_triangle: '倒三角', oval: '椭圆', unknown: '看不出',
  long_legs: '腿偏长', balanced: '均衡', long_torso: '躯干偏长',
  very_light: '很白', light: '白', medium: '中', tan: '小麦', deep: '深', very_deep: '很深',
  warm: '暖', cool: '冷', neutral: '中性', olive: '橄榄',
  black: '黑', dark_brown: '深棕', brown: '棕', blonde: '金', red: '红', gray: '灰', white: '白', dyed_other: '染其他色',
  buzz: '寸头', short: '短', long: '长',
  straight: '直', wavy: '微卷', curly: '卷', coily: '紧密卷',
  low: '少', high: '多',
  round: '圆', square: '方', oblong: '长', heart: '心形', diamond: '菱形',
  delicate: '精致', bold: '量感重',
};

function show(value: number[] | string | undefined) {
  if (Array.isArray(value)) return `${value[0]}–${value[1]}`;
  if (!value) return '—';
  return WORDS[value] || value;
}

const CHOICES = [
  { id: 'qwen3.7-plus', title: 'Qwen', profile: 'qwen' },
  { id: 'seed-lite', title: 'Seed', profile: 'seed' },
  { id: 'gemini-3.8-flash', title: 'Gemini', profile: 'gemini' },
] as const;

function labelOf(key: string) {
  return FIELDS.find((field) => field.key === key)?.label || key;
}

export function AppearancePage() {
  const [split, setSplit] = useState<DatasetSplit>(() => (sessionStorage.getItem('appearance-split') === 'test' ? 'test' : 'train'));
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [trainTotal, setTrainTotal] = useState(0);
  const [testTotal, setTestTotal] = useState(0);
  const [openTotal, setOpenTotal] = useState(0);
  const [passed, setPassed] = useState(0);
  const [sample, setSample] = useState<AppearanceSample | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [picks, setPicks] = useState<Record<string, string>>({});

  const load = useCallback(async (next: number) => {
    setLoading(true);
    setError(null);
    try {
      const page = await listAppearance(next, split);
      const clamped = page.total === 0 ? 0 : Math.min(next, page.total - 1);
      if (clamped !== next) {
        setOffset(clamped);
        return;
      }
      setOffset(page.offset);
      setTotal(page.total);
      setOpenTotal(page.open_total);
      setPassed(page.passed_total || 0);
      setTrainTotal(page.train_total || 0);
      setTestTotal(page.test_total || 0);
      setSample(page.sample);
      setPicks(page.sample?.field_picks || {});
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [split]);

  useEffect(() => {
    load(offset);
  }, [offset, tick, load]);

  const move = (delta: number) => {
    setOffset((current) => Math.min(Math.max(current + delta, 0), Math.max(total - 1, 0)));
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight' || event.key === ' ') {
        event.preventDefault();
        move(1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total]);

  const diffs = sample?.diffs || [];
  const ready = diffs.length > 0 && diffs.every((key) => picks[key]);

  const save = async () => {
    if (!sample || saving || !ready) return;
    setSaving(true);
    const fields: Record<string, string> = {};
    for (const key of diffs) fields[key] = picks[key];
    try {
      await pickAppearance(sample.id, fields);
      if (offset >= total - 1) setTick((value) => value + 1);
      else move(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="min-h-12 px-4 border-b border-[var(--line)] flex items-center gap-3 shrink-0 overflow-x-auto">
        <span className="font-medium">外观差异</span>
        <div className="flex items-center shrink-0 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)]">
          <button
            className={`btn-ghost shrink-0 whitespace-nowrap rounded-r-none px-2 text-sm ${split === 'train' ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
            type="button"
            onClick={() => {
              if (split === 'train') return;
              sessionStorage.setItem('appearance-split', 'train');
              setSplit('train');
              setOffset(0);
            }}
          >
            训练集{trainTotal ? ` ${trainTotal}` : ''}
          </button>
          <button
            className={`btn-ghost shrink-0 whitespace-nowrap rounded-l-none px-2 text-sm ${split === 'test' ? 'bg-[var(--ink)] text-[var(--paper)]' : ''}`}
            type="button"
            onClick={() => {
              if (split === 'test') return;
              sessionStorage.setItem('appearance-split', 'test');
              setSplit('test');
              setOffset(0);
            }}
          >
            测试集{testTotal ? ` ${testTotal}` : ''}
          </button>
        </div>
        <span className="text-sm text-[var(--muted)]">{openTotal === 0 ? '已标完' : `未标注 ${openTotal}`} · 模型通过 {passed}</span>
        <button type="button" className="btn-ghost" onClick={() => move(-1)} disabled={offset <= 0}><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-sm tabular-nums">{total === 0 ? 0 : offset + 1} / {total}</span>
        <button type="button" className="btn-ghost" onClick={() => move(1)} disabled={offset >= total - 1}><ChevronRight className="w-4 h-4" /></button>
        <span className="text-xs text-[var(--muted)]">← → 翻页</span>
      </div>
      {loading && <div className="flex-1 flex items-center justify-center text-[var(--muted)]"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {!loading && error && <div className="p-6 text-sm text-[var(--red)]">{error}</div>}
      {!loading && !error && !sample && <div className="p-6 text-sm text-[var(--muted)]">没有还要对不上的图。</div>}
      {!loading && !error && sample && (
        <div className="flex-1 min-h-0 grid grid-cols-[minmax(280px,42%)_1fr] gap-4 p-4">
          <div className="min-h-0 rounded-xl bg-[var(--paper)] border border-[var(--line)] overflow-hidden flex items-center justify-center">
            <img src={appearanceImageUrl(sample.id)} alt="" className="max-h-full max-w-full object-contain" />
          </div>
          <div className="min-h-0 overflow-auto flex flex-col gap-3">
            <div className="text-sm text-[var(--muted)]">{sample.split === 'test' ? '测试集' : '训练集'} · {sample.sample_key} · 只标下面这些对不上的项，每项单独选。其余字段已经按多数通过。年龄、身高、体重和围度三个对不上时已取中间那段。其余标签对不上的已采用 Gemini。肤色底调对不上的已取中性。</div>
            <div className="flex flex-col gap-3">
              {diffs.map((key) => (
                <section key={key} className="rounded-xl border border-[var(--line)] p-3">
                  <h3 className="text-sm font-medium mb-2">{labelOf(key)}</h3>
                  <div className="flex flex-wrap gap-2">
                    {CHOICES.map((choice) => {
                      const selected = picks[key] === choice.id;
                      return (
                        <button
                          key={choice.id}
                          type="button"
                          className={selected ? 'btn-primary text-sm' : 'btn-ghost text-sm'}
                          onClick={() => setPicks((current) => ({ ...current, [key]: choice.id }))}
                        >
                          {choice.title} {show(sample[choice.profile][key])}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={picks[key] === 'neither' ? 'btn-primary text-sm' : 'btn-ghost text-sm'}
                      onClick={() => setPicks((current) => ({ ...current, [key]: 'neither' }))}
                    >
                      三个都不对
                    </button>
                  </div>
                </section>
              ))}
            </div>
            <div>
              <button type="button" className="btn-primary text-sm" disabled={saving || !ready} onClick={save}>
                {saving ? '保存中' : `这 ${diffs.length} 项标完，下一张`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
